// src/components/mdx/client/visualizations/RetentionCurveChart.client.tsx

/**
 * Shared video retention curve chart with thumbstop and ThruPlay markers.
 */

'use client'

import { useId, useMemo } from 'react'

import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from '~/lib/charts/client'
import type { NameType, TooltipContentType, ValueType } from '~/lib/charts/types'
import { getOrCreateTooltipWrapper, type CustomTooltipProps } from '~/lib/charts/tooltip'
import { AXIS_TOKENS, MARKETING_DATA_VIZ_PHASES, MARKETING_DATA_VIZ_SERIES } from '~/components/mdx/client/visualizations/blogChartPalette'
import { BLOG_CHART_THROTTLE_MS, BlogChartTooltipShell, getBlogChartMargin } from '~/components/mdx/client/visualizations/blogChartLayout'
import type { RetentionPoint } from '~/components/mdx/client/visualizations/marketingDataVizData'
import { useMediaQuery } from '~/lib/hooks/useMediaQuery'

/**
 * Props for RetentionCurveChart.
 *
 * @typedef {Object} RetentionCurveChartProps
 * @property {RetentionPoint[]} data - Retention series.
 * @property {boolean} isActive - Whether animations should run.
 * @property {boolean} [showLegend=false] - Whether to show the series legend.
 */
export interface RetentionCurveChartProps {
  data: RetentionPoint[]
  isActive: boolean
  showLegend?: boolean
}

function RetentionTooltip({ active, payload, label }: CustomTooltipProps): JSX.Element | null {
  if (!active || !payload?.length) {
    return null
  }
  const value = typeof payload[0]?.value === 'number' ? payload[0].value.toFixed(1) : payload[0]?.value
  return (
    <BlogChartTooltipShell title={`${label}s`}>
      <p className="text-gray-300">
        Viewers: <span className="font-semibold text-white">{value}%</span>
      </p>
    </BlogChartTooltipShell>
  )
}

/**
 * Renders a retention curve with benchmark markers.
 *
 * @param {RetentionCurveChartProps} props - Chart props.
 * @returns {JSX.Element} Retention line chart.
 */
export function RetentionCurveChart({ data, isActive, showLegend = false }: RetentionCurveChartProps): JSX.Element {
  const areaGradientId = useId().replace(/:/g, '')
  const isMobile = useMediaQuery('(max-width: 768px)')
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const tooltip = useMemo(() => getOrCreateTooltipWrapper(RetentionTooltip), [])
  const margin = getBlogChartMargin(isMobile, { top: 8, right: isMobile ? 10 : 18, bottom: isMobile ? 8 : 18, left: isMobile ? 0 : 8 })
  const animate = isActive && !prefersReducedMotion

  return (
    <div className="flex h-full flex-col gap-2">
      {showLegend ? (
        <div className="flex flex-wrap items-center justify-center gap-2 text-[11px] sm:text-xs" aria-hidden="true">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-500/10 px-2.5 py-1 font-medium text-violet-300">
            <span className="h-1.5 w-4 rounded-full bg-violet-400" /> Viewers %
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-500/10 px-2.5 py-1 text-orange-300">
            <span className="h-3 border-l border-dashed border-orange-400" /> Thumbstop 3s
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-cyan-500/10 px-2.5 py-1 text-cyan-300">
            <span className="h-3 border-l border-dashed border-cyan-300" /> ThruPlay 15s
          </span>
        </div>
      ) : null}
      <div className="min-h-0 flex-1">
        <ResponsiveContainer width="100%" height="100%" debounce={300} initialDimension={{ width: 800, height: 480 }}>
          <ComposedChart data={data} margin={margin} accessibilityLayer throttleDelay={BLOG_CHART_THROTTLE_MS}>
        <defs>
          <linearGradient id={areaGradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={MARKETING_DATA_VIZ_SERIES.retention} stopOpacity={0.35} />
            <stop offset="95%" stopColor={MARKETING_DATA_VIZ_SERIES.retention} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke={AXIS_TOKENS.grid} strokeDasharray="3 3" />
        <XAxis
          dataKey="second"
          stroke={AXIS_TOKENS.stroke}
          fontSize={isMobile ? 10 : 12}
          label={isMobile ? undefined : { value: 'Seconds', position: 'insideBottom', offset: -8, fill: AXIS_TOKENS.stroke }}
        />
        <YAxis
          stroke={AXIS_TOKENS.stroke}
          fontSize={isMobile ? 10 : 12}
          domain={[0, 100]}
          tickFormatter={value => `${value}%`}
        />
        <Tooltip content={tooltip as TooltipContentType<ValueType, NameType>} />
        <ReferenceArea x1={0} x2={3} fill={MARKETING_DATA_VIZ_SERIES.benchmark} fillOpacity={0.1} />
        <ReferenceArea x1={14} x2={16} fill={MARKETING_DATA_VIZ_PHASES.thruplay} />
        <ReferenceLine
          x={3}
          stroke={MARKETING_DATA_VIZ_SERIES.benchmark}
          strokeDasharray="4 4"
          label={undefined}
        />
        <ReferenceLine
          x={15}
          stroke={MARKETING_DATA_VIZ_SERIES.metaRoas}
          strokeDasharray="4 4"
          label={undefined}
        />
        <Area
          type="monotone"
          dataKey="viewers"
          legendType="none"
          fill={`url(#${areaGradientId})`}
          stroke="none"
          animationDuration={animate ? 700 : 0}
        />
        <Line
          type="monotone"
          dataKey="viewers"
          name="Viewers %"
          stroke={MARKETING_DATA_VIZ_SERIES.retention}
          strokeWidth={2.5}
          dot={false}
          animationDuration={animate ? 700 : 0}
        />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
